import { useDashboard } from "@/modules";
import { useAuth } from "@/modules/auth";
import { IFRAME_DEFAULT_HEIGHT } from "@/constants/widget";
import React, { useEffect, useMemo, useState, useRef } from "react";

type IframeWidgetData = {
    iframeUrl?: string;
    startDate?: string;
    endDate?: string;
    params?: Record<string, unknown>;
};

type IframeWidgetProps = {
    id: number;
    title: string;
    data: IframeWidgetData;
    metadata?: Record<string, unknown>;
};

const isSafeIframeUrl = (value?: string) => {
    if (!value) return false;
    try {
        const url = new URL(value);
        return url.protocol === "https:" || url.protocol === "http:";
    } catch {
        return false;
    }
};

const replaceUrlParams = (template: string, params: Record<string, unknown>) => {
    return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (_match, key: string) => {
        const value = params[key];
        if (value === undefined || value === null) return `{${key}}`;
        return encodeURIComponent(String(value));
    });
};

export const IframeWidget: React.FC<IframeWidgetProps> = ({ id, title, data, metadata }) => {
    const [loaded, setLoaded] = useState(false);
    const { mapToken, user } = useAuth();
    const { state: { selectedDealer } } = useDashboard();

    // Get custom height from metadata (priority), then data params, fallback to default constant
    const customHeight: string = (metadata?.height as string) || IFRAME_DEFAULT_HEIGHT;

    const safeUrl = useMemo(() => {
        if (!data?.iframeUrl) return null;

        const mergedParams: Record<string, unknown> = {
            ...(data?.params ?? {}),
            startDate: data.startDate,
            endDate: data.endDate,
            token: mapToken,
            accessToken: mapToken,
            userGuid: user?.userId,
            dealerId: selectedDealer?.id ?? 'NOT_FOUND',
            dealerRefId: selectedDealer?.dealerRefId ?? 'NOT_FOUND',
            widgetId: id
        };

        const replacedUrl = replaceUrlParams(data.iframeUrl, mergedParams);
        if (!isSafeIframeUrl(replacedUrl)) return null;
        return replacedUrl;
    }, [
        data?.iframeUrl,
        data?.params,
        data?.startDate,
        data?.endDate,
        mapToken,
        user?.userId,
        selectedDealer?.id,
        selectedDealer?.dealerRefId,
        id,
    ]);

    useEffect(() => {
        setLoaded(false);
    }, [safeUrl]);

    if (!safeUrl) {
        return null;
    }

    return (
        <div className="overflow-hidden rounded-b-[16px] -mx-px -mb-1 border border-[#c9d1d4]">
            <iframe
                className="w-full h-auto block rounded-b-[16px]"
                key={safeUrl}
                src={safeUrl}
                title={title}
                loading="lazy"
                referrerPolicy="no-referrer"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                onLoad={() => setLoaded(true)}
                style={{ 
                    height: customHeight,
                    border: 'none !important',
                    borderWidth: '0 !important',
                    outline: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'block'
                }}
            />
        </div>
    );
};
